import { View, StyleSheet, Text } from 'react-native';
import { Scorecard, ScorecardRow, theme } from '@socrait/react';

import {
  ScorecardType,
  StudentWithCount,
  StudentWithAttendance,
  SessionStatus,
  AttendanceData,
} from '~/services/types';
import { ReviewScorecardsResponse } from '~/services/review/useReviewScorecardsQuery';

import { AttendanceScorecard } from '~/pages/review/ui/AttendanceScorecard';

const sortStudentData = <T extends StudentWithCount | StudentWithAttendance>(data: T[]) => {
  return data.slice().sort((a, b) => {
    if (!!(a as StudentWithCount).count && !!(b as StudentWithCount).count) {
      // Primary sort: by count (descending)
      const countDiff = (b as StudentWithCount).count - (a as StudentWithCount).count;
      if (countDiff !== 0) return countDiff;
    }

    // Secondary sort: alphabetically by name (ascending)
    return a.student.voiceName.localeCompare(b.student.voiceName);
  });
};

const getAttendanceData = (data: StudentWithAttendance[]): AttendanceData[] => {
  const present = sortStudentData(data.filter((item) => item.status === 'present'));
  const absent = sortStudentData(data.filter((item) => item.status === 'absent'));
  // TODO: uncomment for phase 3
  // const unsure = sortStudentData(data.filter((item) => item.status === 'unsure'));

  return [
    { attendanceType: 'present', title: 'Present', data: present },
    { attendanceType: 'absent', title: 'Absent', data: absent },
    // TODO: uncomment for phase 3
    // { attendanceType: 'unsure', title: 'Unsure', data: unsure },
  ];
};

export const getDisplayText = (
  type: ScorecardType,
  sessionStatus?: SessionStatus,
  attendanceData?: AttendanceData[],
  total?: number,
) => {
  if (sessionStatus === 'IN_PROGRESS') {
    return 'Session in progress...';
  } else if (sessionStatus === 'PROCESSING') {
    return 'Processing...';
  } else if (sessionStatus === 'COMPLETE') {
    if (type === 'attendance') {
      const presentData = attendanceData?.find((datum) => datum.attendanceType === 'present')?.data;

      if (presentData?.length && presentData?.length === total) {
        return 'All students are present';
      }

      return 'No attendance data collected';
    }
    return 'None detected';
  } else {
    return 'Waiting for data';
  }
};

const getScorecardVariant = (type: ScorecardType) => {
  switch (type) {
    case 'attendance':
      return 'navy';
    case 'warnings':
      return 'yellow';
    case 'praises':
      return 'purple';
    default:
      return 'navy';
  }
};

const getBehaviorTotal = (data?: StudentWithCount[]) => {
  if (!data) return undefined;

  return data.reduce((acc, curr) => {
    const { count } = curr as StudentWithCount;
    return acc + (count || 0);
  }, 0);
};

const renderScorecard = (
  title: string,
  type: ScorecardType,
  sessionStatus?: SessionStatus,
  data?: StudentWithCount[] | AttendanceData[],
) => {
  if (type === 'attendance') {
    return <AttendanceScorecard attendanceData={data as AttendanceData[]} sessionStatus={sessionStatus} />;
  }

  const scorecardVariant = getScorecardVariant(type);
  const total = getBehaviorTotal(data as StudentWithCount[]);

  return (
    <Scorecard title={title} variant={scorecardVariant} total={total}>
      {sessionStatus === 'COMPLETE' && !!total ? (
        data?.map((datum, _index) => {
          const name = (datum as StudentWithCount).student.voiceName;
          const key = (datum as StudentWithCount).student.id;
          const count = (datum as StudentWithCount).count;

          return <ScorecardRow key={`${type}-${key}`} rowId={key} text={name} count={count} />;
        })
      ) : (
        <Text style={styles.noDataText}>{getDisplayText(type, sessionStatus, data as AttendanceData[], total)}</Text>
      )}
    </Scorecard>
  );
};

interface ReviewScoreCardsProps {
  scorecardData?: ReviewScorecardsResponse | null;
}

export const ReviewScoreCards = ({ scorecardData }: ReviewScoreCardsProps) => {
  const scorecards = scorecardData?.scorecards;
  const sessionStatus = scorecardData?.session?.status;

  const sortedScorecards = {
    warnings: scorecards?.warnings ? sortStudentData(scorecards.warnings as StudentWithCount[]) : undefined,
    praises: scorecards?.praises ? sortStudentData(scorecards.praises as StudentWithCount[]) : undefined,
  };

  const { warnings, praises } = sortedScorecards;
  const attendanceData = scorecards?.attendance
    ? getAttendanceData(scorecards?.attendance as StudentWithAttendance[])
    : undefined;

  return (
    <View style={styles.container}>
      <View style={styles.scoreCardContainer}>
        <View style={styles.scoreCard}>{renderScorecard('Warned', 'warnings', sessionStatus, warnings)}</View>
        <View style={styles.scoreCard}>{renderScorecard('Praised', 'praises', sessionStatus, praises)}</View>
      </View>
      <View style={styles.attendanceContainer}>
        {renderScorecard('Attendance', 'attendance', sessionStatus, attendanceData)}
      </View>
    </View>
  );
};

export const styles = StyleSheet.create({
  container: {
    width: '100%',
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    gap: theme.spacing.medium,
  },
  attendanceContainer: {
    width: '100%',
  },
  scoreCardContainer: {
    width: '100%',
    display: 'flex',
    flexDirection: 'row',
    gap: theme.spacing.medium,
  },
  scoreCard: {
    flex: 1,
  },
  noDataText: {
    fontFamily: theme.fonts.regular,
    fontSize: theme.fontSizes.small,
    fontWeight: theme.fontWeights.regular,
    color: theme.colors.darkGray,
    paddingHorizontal: theme.spacing.large,
    paddingVertical: theme.spacing.small,
  },
});
